import "./navbar.css";

import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";

import logo from "../../img/logo/favicon.png";
import arrow from "../../img/icons/arrow.svg";
import caret from "../../img/icons/caret.svg";
import { SERVICES_NAV } from "../pages/services/servicesContent";

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const toggleResources = (e) => {
    e.stopPropagation();
    setIsResourcesOpen((prev) => {
      const next = !prev;
      if (next) setIsServicesOpen(false);
      return next;
    });
  };

  const toggleServices = (e) => {
    e.stopPropagation();
    setIsServicesOpen((prev) => {
      const next = !prev;
      if (next) setIsResourcesOpen(false);
      return next;
    });
  };

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      // Change background when scrolled more than 50px
      setIsScrolled(scrollTop > 50);
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const openNavbar = (event) => {
      if (event.target.tagName !== "BUTTON") {
        setIsNavbarOpen(false);
      }
    };

    document.addEventListener("click", openNavbar);

    return () => {
      document.removeEventListener("click", openNavbar);
    };
  }, []);

  useEffect(() => {
    const closeDropdowns = () => {
      setIsResourcesOpen(false);
      setIsServicesOpen(false);
    };
    if (isResourcesOpen || isServicesOpen) {
      document.addEventListener("click", closeDropdowns);
      return () => document.removeEventListener("click", closeDropdowns);
    }
  }, [isResourcesOpen, isServicesOpen]);

  // Handle section link clicks with smooth scrolling
  const handleSectionClick = (e, sectionId) => {
    e.preventDefault();

    if (location.pathname !== "/") {
      navigate("/#" + sectionId);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    // Close mobile menu if open
    setIsNavbarOpen(false);
  };

  return (
    <header className={`navbar ${isScrolled ? "scrolled" : ""}`}>
      <div className="navbar-box">
        <Link
          to="/"
          className="navbar-logo"
          onClick={() => {
            window.scroll(0, 0);
          }}
        >
          <img src={logo} alt="logo"></img>
          <p>SERVED</p>
        </Link>

        <ul className="navbar-links">
          <li>
            <Link
              to="/"
              className={location.pathname === "/" ? "active" : ""}
              onClick={() => {
                window.scroll(0, 0);
              }}
            >
              HOME
            </Link>
          </li>

          <li className="navbar-dropdown">
            <button
              type="button"
              className={`navbar-dropdown-trigger ${location.pathname.startsWith("/services") ? "active" : ""}`}
              onClick={toggleServices}
              aria-expanded={isServicesOpen}
              aria-haspopup="true"
            >
              SERVICES
              <img src={caret} alt="caret"></img>
            </button>
            {isServicesOpen && (
              <ul className="navbar-dropdown-menu">
                {SERVICES_NAV.map(({ slug, label }) => (
                  <li key={slug}>
                    <Link
                      to={`/services/${slug}`}
                      onClick={() => setIsServicesOpen(false)}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li className="navbar-dropdown">
            <button
              type="button"
              className={`navbar-dropdown-trigger ${location.pathname.startsWith("/resources") ? "active" : ""}`}
              onClick={toggleResources}
              aria-expanded={isResourcesOpen}
              aria-haspopup="true"
            >
              RESOURCES <img src={caret} alt="caret"></img>
            </button>
            {isResourcesOpen && (
              <ul className="navbar-dropdown-menu">
                <li>
                  <Link
                    to="/faq"
                    onClick={() => {
                      setIsResourcesOpen(false);
                      setIsNavbarOpen(false);
                    }}
                  >
                    Frequently Asked Questions
                  </Link>
                </li>
              </ul>
            )}
          </li>

          <li>
            <Link
              to="/contact"
              onClick={() => {
                window.scroll(0, 0);
              }}
            >
              CONTACT
            </Link>
          </li>
        </ul>

        <button
          className="navbar-mobile-button"
          onClick={() => setIsNavbarOpen(true)}
        ></button>
      </div>

      <div
        className="navbar-mobile"
        style={{
          transform: isNavbarOpen ? "translateY(70px)" : "translateY(-100vh)",
          transition: "200ms",
        }}
      >
        <ul className="navbar-mobile-links">
          <li>
            <Link
              to="/"
              className={location.pathname === "/" ? "active" : ""}
              onClick={() => {
                window.scroll(0, 0);
              }}
            >
              HOME
              <img src={arrow} alt="arrow"></img>
            </Link>
          </li>

          <li className="navbar-mobile-dropdown">
            <button
              type="button"
              className={`navbar-mobile-dropdown-trigger ${location.pathname.startsWith("/services") ? "active" : ""}`}
              onClick={toggleServices}
              aria-expanded={isServicesOpen}
            >
              SERVICES
              <img
                src={arrow}
                alt=""
                style={{
                  transform: isServicesOpen
                    ? "rotateZ(180deg)"
                    : "rotateZ(90deg)",
                }}
              />
            </button>
            {isServicesOpen && (
              <ul className="navbar-mobile-dropdown-menu">
                {SERVICES_NAV.map(({ slug, label }) => (
                  <li key={slug}>
                    <Link
                      to={`/services/${slug}`}
                      onClick={() => setIsServicesOpen(false)}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li className="navbar-mobile-dropdown">
            <button
              type="button"
              className={`navbar-mobile-dropdown-trigger ${location.pathname.startsWith("/resources") ? "active" : ""}`}
              onClick={toggleResources}
              aria-expanded={isResourcesOpen}
            >
              RESOURCES
              <img
                src={arrow}
                alt=""
                style={{
                  transform: isResourcesOpen
                    ? "rotateZ(180deg)"
                    : "rotateZ(90deg)",
                }}
              />
            </button>
            {isResourcesOpen && (
              <ul className="navbar-mobile-dropdown-menu">
                <li>
                  <Link
                    to="/faq"
                    onClick={() => {
                      setIsResourcesOpen(false);
                      setIsNavbarOpen(false);
                    }}
                  >
                    Frequently Asked Questions
                  </Link>
                </li>
              </ul>
            )}
          </li>

          <li>
            <Link
              to="/contact"
              onClick={() => {
                window.scroll(0, 0);
              }}
            >
              CONTACT
              <img src={arrow} alt="arrow"></img>
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}

export default Navbar;
